<script setup>
//FilterGroup.vue
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ConditionRow from './ConditionRow.vue';
import Button from 'next/button/Button.vue';

const props = defineProps({
  groupIndex: {
    type: Number,
    required: true,
  },
  isOnlyGroup: {
    type: Boolean,
    default: false,
  },
  filterTypes: {
    type: Array,
    required: true,
  },
  isFirstOperator: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['remove', 'update:groupOperator']);
const { t } = useI18n();

const filters = defineModel('filters', {
  type: Array,
  required: true,
});

const internalOperator = defineModel('internalOperator', {
  type: String,
  default: 'and',
});

const groupOperator = defineModel('groupOperator', {
  type: String,
  default: null,
});

const isCollapsed = ref(false);

const createDefaultFilter = () => ({
  attributeKey: '',
  filterOperator: 'equal_to',
  values: [],
});

const filtersSummary = computed(() => {
  const validFilters = filters.value.filter(f => f.attributeKey);
  if (validFilters.length === 0) return t('FILTER.NO_FILTERS');
  
  const names = validFilters
    .slice(0, 3)
    .map(f => {
      const filterType = props.filterTypes.find(ft => ft.attributeKey === f.attributeKey);
      return filterType?.label || f.attributeKey;
    });
  
  const remaining = validFilters.length - 3;
  if (remaining > 0) {
    return `${names.join(', ')} +${remaining}`;
  }
  return names.join(', ');
});

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
};

const onOperatorChange = (newOperator) => {
  internalOperator.value = newOperator;
};

const addFilter = () => {
  filters.value.push(createDefaultFilter());
  isCollapsed.value = false;
};

const removeFilter = (index) => {
  if (filters.value.length === 1) {
    filters.value[0] = createDefaultFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const removeGroup = () => {
  emit('remove', props.groupIndex);
};

const selectAnd = () => {
  if (props.isFirstOperator) {
    emit('update:groupOperator', 'and');
  }
};

const selectOr = () => {
  if (props.isFirstOperator) {
    emit('update:groupOperator', 'or');
  }
};
</script>

<template>
  <div class="flex items-start gap-2">
    <!-- Label "Onde" ou Toggle E/OU -->
    <div class="pt-3 min-w-[60px]">
      <span v-if="groupIndex === 0" class="text-xs text-n-slate-11 font-medium">
        Onde
      </span>
      <div
        v-else
        class="inline-flex items-center rounded-md bg-n-slate-2 border border-n-weak overflow-hidden w-full"
        :class="{ 'opacity-60': !isFirstOperator }"
      >
        <button
          type="button"
          :disabled="!isFirstOperator"
          class="flex-1 px-2 py-1 text-xs font-medium transition-colors duration-150 text-center"
          :class="[
            groupOperator === 'and'
              ? 'bg-n-blue-9 text-white'
              : 'text-n-slate-11',
            isFirstOperator && groupOperator !== 'and' ? 'hover:bg-n-slate-3' : '',
            !isFirstOperator ? 'cursor-not-allowed' : 'cursor-pointer'
          ]"
          @click="selectAnd"
        >
          E
        </button>
        <div class="w-px h-4 bg-n-weak" />
        <button
          type="button"
          :disabled="!isFirstOperator"
          class="flex-1 px-2 py-1 text-xs font-medium transition-colors duration-150 text-center"
          :class="[
            groupOperator === 'or'
              ? 'bg-n-blue-9 text-white'
              : 'text-n-slate-11',
            isFirstOperator && groupOperator !== 'or' ? 'hover:bg-n-slate-3' : '',
            !isFirstOperator ? 'cursor-not-allowed' : 'cursor-pointer'
          ]"
          @click="selectOr"
        >
          OU
        </button>
      </div>
    </div>

    <!-- Grupo -->
    <div class="filter-group border border-n-weak rounded-lg bg-n-slate-1 flex-1">
      <!-- Header -->
      <div
        class="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-n-slate-2 transition-colors select-none rounded-t-lg"
        :class="{ 'border-b border-n-weak': !isCollapsed }"
        @click="toggleCollapse"
      >
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <!-- Ícone de expansão -->
          <span
            class="text-n-slate-11 transition-transform duration-200 flex-shrink-0"
            :class="[isCollapsed ? 'i-lucide-chevron-right' : 'i-lucide-chevron-down']"
          />
          
          <!-- Título do grupo -->
          <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide flex-shrink-0">
            {{ t('FILTER.GROUP_LABEL') }} {{ groupIndex + 1 }}
          </span>

          <!-- Badge com contagem -->
          <span class="px-1.5 py-0.5 text-xs rounded bg-n-slate-3 text-n-slate-11 flex-shrink-0">
            {{ filters.length }} {{ filters.length === 1 ? 'filtro' : 'filtros' }}
          </span>

          <!-- Resumo quando colapsado -->
          <span
            v-if="isCollapsed"
            class="text-xs text-n-slate-10 truncate"
            :title="filtersSummary"
          >
            {{ filtersSummary }}
          </span>
        </div>

        <div class="flex items-center gap-1 flex-shrink-0" @click.stop>
          <Button
            v-if="!isOnlyGroup"
            v-tooltip.top="t('FILTER.DELETE_GROUP')"
            xs
            ghost
            ruby
            icon="i-lucide-trash-2"
            @click="removeGroup"
          />
        </div>
      </div>

      <!-- Conteúdo -->
      <div v-show="!isCollapsed" class="p-4 pt-3">
        <ul class="flex flex-col gap-2">
          <ConditionRow
            v-for="(filter, index) in filters"
            :key="`${groupIndex}-${index}`"
            v-model:attribute-key="filter.attributeKey"
            v-model:filter-operator="filter.filterOperator"
            v-model:values="filter.values"
            :query-operator="internalOperator"
            :filter-types="filterTypes"
            :show-query-operator="index > 0"
            :is-first-query-operator="index === 1"
            @update:query-operator="onOperatorChange"
            @remove="removeFilter(index)"
          />
        </ul>

        <div class="mt-3">
          <Button
            sm
            ghost
            blue
            icon="i-lucide-plus"
            :label="t('FILTER.ADD_FILTER')"
            @click="addFilter"
          />
        </div>
      </div>
    </div>
  </div>
</template>